import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from '@mui/material';
import BoxButton from '@/components/@IntranetLibrary/Button/BoxButton';
import MobileBottomSheet from '@/components/@IntranetLibrary/MobileBottomSheet';
import Modal, { type ModalRef } from '@/components/@IntranetLibrary/Modal';
import Spinner from '@/components/@IntranetLibrary/Spinner';
import SerpContentSnapshot from '@/components/Approval/@components/SerpContentSnapshot';
import type { ElementTransform } from '@/components/Common/HtmlToReact';
import {
  getClaimClosingDraftSourceUrl,
  getGerpProductionDetailUrl,
} from '@/constant/approval';
import { useSnackbar } from '@/hooks/global-tools';
import useLayoutSize from '@/hooks/useLayoutSize';
import { getNestedDataFetcher } from '@/utils/apis';
import type {
  ClaimClosingDetail,
  ClaimClosingDraftSource,
} from '@/utils/approval/claimClosing';
import {
  DELETE_BUTTON_CLASS,
  DETAIL_BUTTON_CLASS,
  REJECT_BUTTON_CLASS,
} from './content';
import ClaimClosingDetailTable from './DetailTable';
import { ClaimClosingDetailSheetBody, ClaimClosingRoot } from './styles';

export { buildClaimClosingHtml } from './content';

/**
 * 상세보기 조회 경로 — 작성 중 고른 건은 draft-source,
 * 저장된 문서(편집·조회)는 문서번호(gerp-detail, 상신 건은 미상신 목록에서 빠짐)
 */
export type ClaimClosingDetailSource =
  | { kind: 'draft' }
  | { kind: 'document'; documentId: string };

export type ClaimClosingTarget = { itemId: string; docTit: string };

/** 부분반려 버튼 수 — 레거시처럼 1개뿐이면 부분반려 숨김 */
export const countClaimClosingCards = (content: string | null) =>
  content
    ? (content.match(new RegExp(REJECT_BUTTON_CLASS, 'g')) ?? []).length
    : 0;

const fetchDetail = async (
  source: ClaimClosingDetailSource,
  approvalId: string,
): Promise<ClaimClosingDetail | null> => {
  if (source.kind === 'draft') {
    const data = await getNestedDataFetcher<ClaimClosingDraftSource>(
      getClaimClosingDraftSourceUrl([approvalId], approvalId),
    );
    return data.display.detail;
  }
  const data = await getNestedDataFetcher<{
    detail: ClaimClosingDetail | null;
  }>(getGerpProductionDetailUrl(source.documentId, { approvalId }));
  return data.detail;
};

/** 「상세내역」 — PC 모달, 모바일 바텀시트. 불러오는 동안 로딩 */
const DetailDialog = ({
  open,
  detail,
  onClose,
}: {
  open: boolean;
  detail: ClaimClosingDetail | null;
  onClose: () => void;
}) => {
  const { t } = useTranslation();
  const { isMobile } = useLayoutSize();
  const modalRef = useRef<ModalRef>(null);

  useEffect(() => {
    if (isMobile) return;
    if (open) modalRef.current?.open();
    else modalRef.current?.close();
  }, [open, isMobile]);

  const body = open ? (
    <ClaimClosingRoot>
      {detail ? (
        <ClaimClosingDetailTable lines={detail.lines ?? []} />
      ) : (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <Spinner theme="black" size="24px" />
        </Box>
      )}
    </ClaimClosingRoot>
  ) : null;

  if (isMobile) {
    return (
      <MobileBottomSheet
        open={open}
        onClose={onClose}
        title={t('상세내역')}
        height="auto"
        maxHeight="80vh"
        swipeable={false}
      >
        <ClaimClosingDetailSheetBody>{body}</ClaimClosingDetailSheetBody>
      </MobileBottomSheet>
    );
  }
  return (
    <Modal
      ref={modalRef}
      title={t('상세내역')}
      handleClose={onClose}
      isExpandable
    >
      <>{body}</>
    </Modal>
  );
};

/**
 * 작성·조회 공용 — 저장 카드 HTML 그대로(레거시 문서 포함).
 * 「상세보기」는 그때 다시 조회해 창으로, 「삭제」는 작성 화면만, 「부분반려」는 조회 화면만
 */
export const ClaimClosingSnapshot = ({
  content,
  detailSource,
  onDelete,
  renderCardAction,
}: {
  content: string;
  /** 없으면 상세보기 버튼을 그리지 않음 */
  detailSource: ClaimClosingDetailSource | null;
  onDelete?: (target: ClaimClosingTarget) => void;
  renderCardAction?: (target: ClaimClosingTarget) => ReactNode;
}) => {
  const { t } = useTranslation();
  const { showError } = useSnackbar();
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState<ClaimClosingDetail | null>(null);
  // 닫은 뒤 늦게 온 응답이 창을 다시 열지 않게
  const requestIdRef = useRef(0);

  const close = useCallback(() => {
    requestIdRef.current += 1;
    setOpen(false);
    setDetail(null);
  }, []);

  const openDetail = useCallback(
    async (approvalId: string) => {
      if (!detailSource || !approvalId) return;
      requestIdRef.current += 1;
      const requestId = requestIdRef.current;
      setDetail(null);
      setOpen(true);
      try {
        const next = await fetchDetail(detailSource, approvalId);
        if (requestId !== requestIdRef.current) return;
        if (!next) throw new Error('detail missing');
        setDetail(next);
      } catch {
        if (requestId !== requestIdRef.current) return;
        close();
        showError({ message: t('데이터를 불러오지 못했습니다.') });
      }
    },
    [close, detailSource, showError, t],
  );

  const transform = useCallback<ElementTransform>(
    ({ element, tagName, props }) => {
      if (tagName !== 'button') return undefined;
      const { classList } = element;
      const approvalId = element.getAttribute('data-aprv_id') ?? '';
      if (classList.contains(REJECT_BUTTON_CLASS)) {
        if (!renderCardAction || !approvalId) return null;
        return (
          renderCardAction({
            itemId: approvalId,
            docTit: element.getAttribute('data-aprv_doctit') ?? '',
          }) ?? null
        );
      }
      if (classList.contains(DELETE_BUTTON_CLASS)) {
        if (!onDelete || !approvalId) return null;
        const docTit = element.getAttribute('data-aprv_doctit') ?? '';
        return (
          <BoxButton
            key={props.key as string}
            type="button"
            variant="secondary"
            size="small"
            onClick={() => onDelete({ itemId: approvalId, docTit })}
          >
            {t('삭제')}
          </BoxButton>
        );
      }
      if (classList.contains(DETAIL_BUTTON_CLASS)) {
        if (!detailSource) return null;
        return (
          <BoxButton
            key={props.key as string}
            type="button"
            variant="secondary"
            size="small"
            onClick={() => openDetail(approvalId)}
          >
            {t('상세보기')}
          </BoxButton>
        );
      }
      return undefined;
    },
    [detailSource, onDelete, openDetail, renderCardAction, t],
  );

  return (
    <>
      <ClaimClosingRoot>
        <SerpContentSnapshot content={content} transform={transform} />
      </ClaimClosingRoot>
      <DetailDialog open={open} detail={detail} onClose={close} />
    </>
  );
};
